import { useCallback, useRef } from "react";
import { Upload, FileSpreadsheet, X, AlertCircle, Download } from "lucide-react";
import { Loader } from "@/ui/atoms";
import { cn } from "@/utils/utils";
import { PROPCODE_REQUIRED_COLUMNS } from "./constants";
import type { TPropcodeMappingData } from "./constants";

export type TPropcodeMappingUploadSectionProps = {
    mappingData: TPropcodeMappingData;
    onFileSelect: (file: File) => void;
    onClearFile: () => void;
    /** When set, a download button is shown next to the loaded file (e.g. copied mappings). */
    onDownloadFile?: () => void;
    /** The file's columns, for a customer module whose file differs. Defaults to Rev EU's. */
    requiredColumns?: readonly string[];
    /** False hides the Level column, for a customer module whose mappings have no level. */
    showLevel?: boolean;
};

export function PropcodeMappingUploadSection({
    mappingData,
    onFileSelect,
    onClearFile,
    onDownloadFile,
    requiredColumns = PROPCODE_REQUIRED_COLUMNS,
    showLevel = true,
}: TPropcodeMappingUploadSectionProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            onFileSelect(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();

            const file = e.dataTransfer.files?.[0];
            if (file) {
                onFileSelect(file);
            }
        },
        [onFileSelect],
    );

    const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <div className="space-y-4">
            <label
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={cn(
                    "block border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
                    mappingData.file
                        ? "border-primary bg-primary/5"
                        : "border-muted-foreground/25 hover:border-primary/50 hover:bg-primary/5",
                )}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={mappingData.isLoading}
                />
                {mappingData.isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                        <Loader size="sm" />
                        <span className="text-sm text-muted-foreground">Validating file...</span>
                    </div>
                ) : mappingData.file ? (
                    <div className="flex items-center justify-center gap-3">
                        <FileSpreadsheet className="h-8 w-8 text-primary" />
                        <div className="text-left">
                            <p className="font-medium text-sm">{mappingData.file.name}</p>
                            <p className="text-xs text-muted-foreground">
                                {mappingData.rows.length} mappings loaded
                            </p>
                        </div>
                        {onDownloadFile && (
                            <button
                                type="button"
                                title="Download file"
                                aria-label="Download file"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onDownloadFile();
                                }}
                                className="ml-4 p-1 hover:bg-muted rounded"
                            >
                                <Download className="h-4 w-4" />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onClearFile();
                            }}
                            className={cn("p-1 hover:bg-muted rounded", !onDownloadFile && "ml-4")}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <div>
                        <Upload className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
                        <p className="text-sm font-medium text-muted-foreground mb-1">
                            Drag and drop your Excel file here, or{" "}
                            <span className="text-primary hover:underline">click to browse</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-3">
                            Required columns: {requiredColumns.join(", ")}
                        </p>
                    </div>
                )}
            </label>

            {mappingData.error && (
                <div className="flex items-start gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{mappingData.error}</span>
                </div>
            )}

            {mappingData.warnings.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg text-sm">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                        <p className="font-medium">Warnings:</p>
                        <ul className="list-disc list-inside text-xs mt-1">
                            {mappingData.warnings.slice(0, 5).map((w, i) => (
                                <li key={i}>{w}</li>
                            ))}
                            {mappingData.warnings.length > 5 && (
                                <li>...and {mappingData.warnings.length - 5} more</li>
                            )}
                        </ul>
                    </div>
                </div>
            )}

            <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 sticky top-0">
                            <tr>
                                {showLevel && (
                                    <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap text-xs">
                                        Level
                                    </th>
                                )}
                                <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap text-xs">
                                    Source Propcode
                                </th>
                                <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap text-xs">
                                    Propcode
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {mappingData.rows.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={showLevel ? 3 : 2}
                                        className="px-4 py-8 text-center text-muted-foreground text-sm"
                                    >
                                        {mappingData.file
                                            ? "No mappings found in file"
                                            : "No data uploaded yet"}
                                    </td>
                                </tr>
                            ) : (
                                mappingData.rows.slice(0, 100).map((row, idx) => (
                                    <tr
                                        key={idx}
                                        className="border-t hover:bg-muted/30 even:bg-muted/40"
                                    >
                                        {showLevel && (
                                            <td className="px-4 py-2 whitespace-nowrap text-xs">
                                                {row.level || (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </td>
                                        )}
                                        <td className="px-4 py-2 whitespace-nowrap text-xs">
                                            {row.source_propcode || (
                                                <span className="text-muted-foreground italic">
                                                    (blank)
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-xs">
                                            {row.target_propcode || (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {mappingData.rows.length > 100 && (
                    <div className="px-4 py-2 bg-muted/30 text-xs text-muted-foreground border-t">
                        Showing first 100 of {mappingData.rows.length} mappings
                    </div>
                )}
            </div>
        </div>
    );
}
